import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  TableSortLabel,
  IconButton,
  InputAdornment,
  Checkbox,
  Toolbar,
  Tooltip,
  Collapse,
  Alert,
  Snackbar,
  TablePagination,
} from '@mui/material'
import { Add, Refresh, Search, ArrowForward, Delete, FilterAlt } from '@mui/icons-material'
import { useApi } from '../../api/client'
import { fetchRecords, batchDeleteRecords, type RecordRow } from '../../api/records'
import { useObjectQueries } from '../../api/queries'
import { useTabStore } from '../../stores/tab.store'
import { RecordFormDialog } from '../../components/shared/RecordFormDialog'
import { ConfirmDialog } from '../../components/shared/ConfirmDialog'
import { FieldRenderer } from '../../components/fields/FieldRenderer'
import type { FieldMeta, ApiResponse } from '@crm/shared'

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debouncedValue
}

interface FilterCondition {
  field: string
  op: string
  value: string
}

export function ObjectWorkList() {
  const { objectId } = useParams<{ objectId: string }>()
  const navigate = useNavigate()
  const { openTab } = useTabStore()

  const { data: schemaData, isLoading: schemaLoading } = useApi<
    ApiResponse<{
      object: { id: string; label: string; label_plural: string; name: string; ti_header_fields: string | null }
      fields: FieldMeta[]
      object_permission: string
      field_permissions: Record<string, string>
    }>
  >(objectId ? `/objects/${objectId}/schema` : null)

  const objectInfo = schemaData?.data?.object
  const fields = schemaData?.data?.fields ?? []
  const objectPermission = schemaData?.data?.object_permission ?? 'none'
  const fieldPermissions = schemaData?.data?.field_permissions ?? {}

  const visibleFields = fields.filter((field) => fieldPermissions[field.field_id] !== 'hidden' && field.is_owl_visible)

  const [records, setRecords] = useState<RecordRow[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(20)
  const [loading, setLoading] = useState(false)
  const [recordsError, setRecordsError] = useState('')
  const [keyword, setKeyword] = useState('')
  const debouncedKeyword = useDebouncedValue(keyword, 300)
  const [selectedQueryId, setSelectedQueryId] = useState<string>('')
  const [sortField, setSortField] = useState('updated_at')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [createOpen, setCreateOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [filterOpen, setFilterOpen] = useState(false)
  const [filters, setFilters] = useState<FilterCondition[]>([])
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' })

  const { data: queriesData } = useObjectQueries(objectId ?? null)
  const queries = queriesData?.data ?? []

  const canCreate = objectPermission === 'read_write' || objectPermission === 'full'
  const canDelete = objectPermission === 'full'

  const loadRecords = useCallback(async (targetPage?: number, targetPageSize?: number) => {
    if (!objectId) return
    setLoading(true)
    setRecordsError('')
    try {
      const p = targetPage ?? page
      const ps = targetPageSize ?? pageSize
      const params: Record<string, string> = {}
      if (debouncedKeyword.trim()) params.keyword = debouncedKeyword.trim()
      if (selectedQueryId) params.query_id = selectedQueryId
      params.sort = `${sortField}:${sortDir}`
      params.page = String(p + 1)
      params.page_size = String(ps)
      if (filters.length > 0) {
        params.filters = JSON.stringify({ logic: 'and', conditions: filters.filter((f) => f.field && f.value) })
      }

      const result = await fetchRecords(objectId, params)
      setRecords(result.data)
      setTotalCount(result.pagination.total_count ?? 0)
      setSelectedIds(new Set())
    } catch (err) {
      setRecords([])
      setTotalCount(0)
      setRecordsError(err instanceof Error ? err.message : '加载失败')
    } finally {
      setLoading(false)
    }
  }, [objectId, debouncedKeyword, selectedQueryId, sortField, sortDir, filters, page, pageSize])

  useEffect(() => {
    loadRecords()
  }, [loadRecords])

  useEffect(() => {
    if (objectInfo) {
      openTab({
        objectId: objectInfo.id,
        objectLabel: objectInfo.label_plural || objectInfo.label,
        path: `/o/${objectInfo.id}`,
      })
    }
  }, [objectInfo, openTab])

  const handleRowClick = (record: RecordRow) => {
    if (!objectInfo) return
    openTab({
      objectId: objectInfo.id,
      objectLabel: objectInfo.label,
      recordId: record.id,
      path: `/o/${objectInfo.id}/${record.id}`,
    })
    navigate(`/o/${objectInfo.id}/${record.id}`)
  }

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDir('desc')
    }
    setPage(0)
  }

  const handleSelectAll = () => {
    if (selectedIds.size === records.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(records.map((r) => r.id)))
    }
  }

  const handleSelectRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleBatchDelete = useCallback(async () => {
    if (!objectId || selectedIds.size === 0) return
    try {
      const result = await batchDeleteRecords(objectId, Array.from(selectedIds))
      setToast({ open: true, message: `Deleted ${result.data.success_count} records`, severity: 'success' })
      setSelectedIds(new Set())
      loadRecords()
    } catch (err) {
      setToast({ open: true, message: err instanceof Error ? err.message : 'Delete failed', severity: 'error' })
    }
    setDeleteOpen(false)
  }, [objectId, selectedIds, loadRecords])

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10)
    setPageSize(newPageSize)
    setPage(0)
  }

  const addFilter = () => setFilters((prev) => [...prev, { field: '', op: 'eq', value: '' }])
  const removeFilter = (index: number) => setFilters((prev) => prev.filter((_, i) => i !== index))
  const updateFilter = (index: number, key: keyof FilterCondition, value: string) =>
    setFilters((prev) => prev.map((f, i) => (i === index ? { ...f, [key]: value } : f)))

  const formatCellValue = (field: FieldMeta, record: RecordRow): string => {
    const value = record.data[field.name]
    if (value === null || value === undefined) return ''
    if (field.type === 'boolean') return value ? '✓' : '✗'
    if (field.type === 'multi_select' && Array.isArray(value)) return value.join(', ')
    return String(value)
  }

  const filterableFields = visibleFields.filter((f) => f.type !== 'reverse_lookup')

  if (schemaLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {objectInfo?.label_plural ?? 'Loading...'}
        </Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<Add />} size="small" onClick={() => setCreateOpen(true)}>
            New
          </Button>
        )}
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
        {queries.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Filter View</InputLabel>
            <Select
              value={selectedQueryId}
              label="Filter View"
              onChange={(event) => { setSelectedQueryId(event.target.value); setPage(0) }}
            >
              {queries.map((query) => (
                <MenuItem key={query.id} value={query.id}>{query.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <TextField
          size="small"
          placeholder="Search..."
          value={keyword}
          onChange={(event) => { setKeyword(event.target.value); setPage(0) }}
          sx={{ width: 260 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
            },
          }}
        />

        <Tooltip title="Advanced Filters">
          <IconButton size="small" onClick={() => setFilterOpen((p) => !p)} color={filters.length > 0 ? 'primary' : 'default'}>
            <FilterAlt />
          </IconButton>
        </Tooltip>

        <Box sx={{ flexGrow: 1 }} />

        <IconButton size="small" onClick={() => loadRecords()} disabled={loading}>
          <Refresh />
        </IconButton>
      </Box>

      <Collapse in={filterOpen} sx={{ flexShrink: 0 }}>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2">Filters</Typography>
            <Button size="small" onClick={addFilter}>+ Add Filter</Button>
          </Box>
          {filters.map((filter, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Field</InputLabel>
                <Select value={filter.field} label="Field" onChange={(e) => updateFilter(i, 'field', e.target.value)}>
                  {filterableFields.map((f) => (
                    <MenuItem key={f.field_id} value={f.name}>{f.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Op</InputLabel>
                <Select value={filter.op} label="Op" onChange={(e) => updateFilter(i, 'op', e.target.value)}>
                  <MenuItem value="eq">Equals</MenuItem>
                  <MenuItem value="neq">Not equals</MenuItem>
                  <MenuItem value="contains">Contains</MenuItem>
                  <MenuItem value="gt">Greater than</MenuItem>
                  <MenuItem value="lt">Less than</MenuItem>
                  <MenuItem value="in">In</MenuItem>
                  <MenuItem value="is_empty">Is empty</MenuItem>
                  <MenuItem value="is_not_empty">Not empty</MenuItem>
                </Select>
              </FormControl>
              {!['is_empty', 'is_not_empty'].includes(filter.op) && (
                <TextField size="small" value={filter.value} onChange={(e) => updateFilter(i, 'value', e.target.value)} sx={{ flexGrow: 1 }} />
              )}
              <IconButton size="small" onClick={() => removeFilter(i)}>×</IconButton>
            </Box>
          ))}
          {filters.length > 0 && (
            <Button size="small" color="error" onClick={() => { setFilters([]); setPage(0) }}>Clear All</Button>
          )}
        </Paper>
      </Collapse>

      {selectedIds.size > 0 && (
        <Toolbar variant="dense" sx={{ bgcolor: 'primary.light', borderRadius: 1, mb: 1, minHeight: 40, flexShrink: 0 }}>
          <Typography variant="body2" sx={{ flexGrow: 1 }}>{selectedIds.size} selected</Typography>
          {canDelete && (
            <Button size="small" color="error" startIcon={<Delete />} onClick={() => setDeleteOpen(true)}>
              Delete
            </Button>
          )}
        </Toolbar>
      )}

      <Paper variant="outlined" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {canDelete && (
                  <TableCell padding="checkbox" sx={{ width: 40 }}>
                    <Checkbox
                      indeterminate={selectedIds.size > 0 && selectedIds.size < records.length}
                      checked={records.length > 0 && selectedIds.size === records.length}
                      onChange={handleSelectAll}
                      size="small"
                    />
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>ID</TableCell>
                {visibleFields.slice(0, 8).map((field) => (
                  <TableCell key={field.field_id} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                    <TableSortLabel
                      active={sortField === field.name}
                      direction={sortField === field.name ? sortDir : 'desc'}
                      onClick={() => handleSort(field.name)}
                    >
                      {field.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 700, width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={1 + visibleFields.slice(0, 8).length + (canDelete ? 2 : 1)} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {recordsError && !loading && (
                <TableRow>
                  <TableCell colSpan={1 + visibleFields.slice(0, 8).length + (canDelete ? 2 : 1)} align="center" sx={{ py: 4 }}>
                    <Alert severity="error" sx={{ justifyContent: 'center' }}>{recordsError}</Alert>
                  </TableCell>
                </TableRow>
              )}
              {records.length === 0 && !loading && !recordsError && (
                <TableRow>
                  <TableCell colSpan={1 + visibleFields.slice(0, 8).length + (canDelete ? 2 : 1)} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No records found</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && !recordsError && records.map((record) => (
                <TableRow
                  key={record.id}
                  hover
                  selected={selectedIds.has(record.id)}
                  onClick={() => handleRowClick(record)}
                  sx={{ cursor: 'pointer' }}
                >
                  {canDelete && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.has(record.id)}
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleSelectRow(record.id) }}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={{ color: 'text.secondary' }}>{record.id}</TableCell>
                  {visibleFields.slice(0, 8).map((field) => (
                    <TableCell key={field.field_id} sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {field.type === 'lookup' || field.type === 'multi_lookup'
                        ? <FieldRenderer field={field} value={record.data[field.name]} mode="view" />
                        : formatCellValue(field, record)}
                    </TableCell>
                  ))}
                  <TableCell>
                    <IconButton size="small" onClick={(event) => { event.stopPropagation(); handleRowClick(record) }}>
                      <ArrowForward fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[20, 50, 100]}
          showFirstButton
          showLastButton
          sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider' }}
        />
      </Paper>

      <RecordFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={loadRecords}
        objectId={objectId!}
        fields={fields}
        fieldPermissions={fieldPermissions}
        mode="create"
      />

      <ConfirmDialog
        open={deleteOpen}
        title="Delete Records"
        message={`Are you sure you want to delete ${selectedIds.size} record(s)?`}
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={handleBatchDelete}
        onCancel={() => setDeleteOpen(false)}
      />

      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast((p) => ({ ...p, open: false }))}>
        <Alert severity={toast.severity} onClose={() => setToast((p) => ({ ...p, open: false }))}>{toast.message}</Alert>
      </Snackbar>
    </Box>
  )
}
